import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../services/api-client';

export interface PermissionsContextType {
  allowedRoutes: string[];
  deniedRoutes: string[];
  permissions: Record<string, boolean>;
  role: string | null;
  isSuperAdmin: boolean;
  isLoading: boolean;
  canAccessRoute: (route: string) => boolean;
  hasPermission: (actionKey: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, currentOrg, isAuthenticated } = useAuth();
  const [allowedRoutes, setAllowedRoutes] = useState<string[]>([
    '/',
    '/webmail',
    '/campaigns',
    '/deliverability',
    '/billing',
    '/api-keys',
    '/uam',
    '/health',
  ]);
  const [deniedRoutes, setDeniedRoutes] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [role, setRole] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await apiClient.getUamMyPermissions();
      if (data) {
        setAllowedRoutes(data.allowedRoutes || []);
        setDeniedRoutes(data.deniedRoutes || []);
        setPermissions(data.permissions || {});
        setRole(data.role || null);
        setIsSuperAdmin(Boolean(data.isSuperAdmin));
      }
    } catch (err) {
      console.error('Failed to load user UAM permissions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, currentOrg?.id]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const canAccessRoute = useCallback(
    (route: string): boolean => {
      if (deniedRoutes.includes(route)) return false;
      if (allowedRoutes.includes(route)) return true;
      if (isSuperAdmin) return true;
      return false;
    },
    [isSuperAdmin, allowedRoutes, deniedRoutes],
  );

  const hasPermission = useCallback(
    (actionKey: string): boolean => {
      if (permissions[actionKey] === false) return false;
      if (permissions[actionKey] === true) return true;
      if (isSuperAdmin) return true;
      return false;
    },
    [isSuperAdmin, permissions],
  );

  return (
    <PermissionsContext.Provider
      value={{
        allowedRoutes,
        deniedRoutes,
        permissions,
        role,
        isSuperAdmin,
        isLoading,
        canAccessRoute,
        hasPermission,
        refreshPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
